import axios from 'axios';
import { apiUrl as getAPIUrl, authHeader as getAuthToken } from '../runtime';
import { saveAnswers } from './storage';
import { isPreview, isReadOnly } from './session';
import { emptyDoc } from './emptyDoc';
const base = (bookingId, briefUuid) => getAPIUrl(`/api/booking/creative-preference/${bookingId}/${briefUuid}/`);
const headers = () => ({
  Authorization: getAuthToken()
});

// The brief layer: event types are answered once per brief, not per form.
const briefBase = (bookingId, briefUuid) => getAPIUrl(`/api/booking/brief/${bookingId}/${briefUuid}/`);

// GET brief/<booking_id>/<brief_uuid>/
export const fetchBrief = async ({
  bookingId,
  briefUuid
}) => {
  const {
    data
  } = await axios.get(briefBase(bookingId, briefUuid), {
    headers: headers()
  });
  if (!data.success) throw new Error(data.message);
  return data;
};

// GET creative-preference/<booking_id>/<brief_uuid>/status/
// Returns the saved answer document; `answers` is partial — only the groups the
// client has touched are present, so it must be merged over emptyDoc().
export const fetchStatus = async ({
  bookingId,
  briefUuid
}) => {
  const {
    data
  } = await axios.get(`${base(bookingId, briefUuid)}status/`, {
    headers: headers()
  });
  if (!data.success) throw new Error(data.message);
  return data;
};

// PATCH brief/<booking_id>/<brief_uuid>/event-types/
export const patchEventTypes = async ({
  bookingId,
  briefUuid,
  eventTypes
}) => {
  if (isPreview() || isReadOnly()) return {
    success: true
  };
  const {
    data
  } = await axios.patch(`${briefBase(bookingId, briefUuid)}event-types/`, {
    event_types: eventTypes
  }, {
    headers: headers()
  });
  if (!data.success) throw new Error(data.message);
  return data;
};

// GET creative-preference/<booking_id>/<brief_uuid>/
// Returns the form's whole structure. `sections` is already in display order
// and carries every title, question, option and cap, so nothing about the shape
// of this form is declared in React.
export const bootstrap = async ({
  bookingId,
  briefUuid,
  snapshot,
  summary
}) => {
  // A snapshot serves that round's answers *and* that round's questions.
  const query = new URLSearchParams();
  if (snapshot) query.set('snapshot', snapshot);
  // A read of the answers back, not the form: no moodboard photos, no open.
  if (summary) query.set('summary', '1');
  const url = query.toString() ? `${base(bookingId, briefUuid)}?${query}` : base(bookingId, briefUuid);
  const {
    data
  } = await axios.get(url, {
    headers: headers()
  });
  // Not a failure to retry: the server refused this brief for this user, and
  // the page says so. A thrown error would be indistinguishable from a
  // dropped connection.
  if (data.success === false) {
    return {
      denied: true,
      code: data.code,
      message: data.message
    };
  }
  return {
    ...data,
    sections: data.sections || [],
    answers: {
      // emptyDoc() first, so the envelope keys exist on an untouched form.
      ...emptyDoc(),
      ...(data.answers || {}),
      // The server's status owns the lock. A form ops has reopened comes back
      // as 'in progress', and must unlock even though the answer document it
      // was submitted from still says otherwise.
      submitted: data.status === 'submitted',
      // The deadline, same treatment: a reopened form arrives unlocked.
      locked: data.locked === true
    }
  };
};

// GET creative-preference/preview/?version=<id>
// Ops reading a version before it is published. Same shape bootstrap returns,
// with no brief behind it: no answers, no submissions, nothing to save to.
export const fetchPreview = async ({
  version
}) => {
  const url = getAPIUrl(`/api/booking/creative-preference/preview/?version=${encodeURIComponent(version)}`);
  const {
    data
  } = await axios.get(url, {
    headers: headers()
  });
  if (data.success === false) {
    const err = new Error(data.message);
    err.code = data.code;
    throw err;
  }
  return {
    ...data,
    sections: data.sections || [],
    answers: {
      ...emptyDoc(),
      ...(data.answers || {})
    }
  };
};

// PATCH creative-preference/<booking_id>/<brief_uuid>/section/<section_key>/
// Always carries the section's full current value — see design doc §3.4.
export const patchSection = async ({
  bookingId,
  briefUuid,
  sectionKey,
  payload,
  doc
}) => {
  // Last guard before anything leaves the app: every server write goes through
  // here, so a future caller that skips `write` still cannot save. The preview
  // returns first so the offline mirror below is never written either.
  if (isPreview() || isReadOnly()) return {
    revision: doc.revision
  };
  // Offline mirror, kept whether or not the write reaches the server.
  saveAnswers(briefUuid, doc);
  // No deep link means there is no brief to write to.
  if (!briefUuid || briefUuid === 'local') return {
    revision: doc.revision
  };
  const {
    data
  } = await axios.patch(`${base(bookingId, briefUuid)}section/${sectionKey}/`, payload, {
    headers: headers()
  });
  if (data.success === false) {
    const err = new Error(data.message);
    // Server-side rejection: permanent, never worth retrying.
    err.rejected = true;
    err.code = data.code;
    throw err;
  }
  return data;
};

// POST creative-preference/<booking_id>/<brief_uuid>/submit/
export const submitBrief = async ({
  bookingId,
  briefUuid,
  doc,
  acknowledged
}) => {
  // A preview has nothing to submit, and must not be reported as submitted.
  if (isPreview()) return {
    status: 'preview'
  };
  if (isReadOnly()) return {
    status: 'submitted'
  };
  // Offline mirror, kept whether or not the write reaches the server.
  saveAnswers(briefUuid, doc);
  // No deep link means there is no brief to submit.
  if (!briefUuid || briefUuid === 'local') return {
    status: 'submitted'
  };

  // The server refuses anything but literal true, so the tick gates the call
  // rather than only the styling.
  if (acknowledged !== true) {
    const err = new Error('Please acknowledge the planning note before submitting.');
    err.rejected = true;
    err.code = 'not_acknowledged';
    throw err;
  }
  const {
    data
  } = await axios.post(`${base(bookingId, briefUuid)}submit/`, {
    acknowledged: true
  }, {
    headers: headers()
  });
  if (data.success === false) {
    const err = new Error(data.message);
    // Every submit failure is permanent; none is worth retrying.
    err.rejected = true;
    err.code = data.code;
    err.pendingSections = data.pending_sections || [];
    throw err;
  }
  return data;
};

// GET creative-preference/<booking_id>/<brief_uuid>/media/<option_ref>/
// A moodboard collection's photos. Read when the client opens that card:
// the section itself carries only covers, so a page of collections does
// not pay for photos nobody looks at.
export const fetchCollectionMedia = async ({
  bookingId,
  briefUuid,
  optionRef,
  page = 0
}) => {
  const {
    data
  } = await axios.get(
    `${base(bookingId, briefUuid)}media/${encodeURIComponent(optionRef)}/`,
    {
      headers: headers(),
      params: {
        page
      }
    });
  if (!data.success) throw new Error(data.message);
  return {
    media: data.media || [],
    nextPage: data.next_page ?? null
  };
};
